/**
 * Simple form validation
 */

import { slideUp, slideDown } from './slide-up-down.js';
import { debounce } from '@assets/js/utils.js';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Add support for custom parse formats for dayjs()
dayjs.extend(customParseFormat);

let options = {
  setFocus: true,
  setInvalidClass: true,
  showFieldErrors: true,
  onlyShowFirstError: true,
}

export function initFormEvents(form, options = {}) {
  // Add 'onchange' handlers to validate fields and mark the current step as valid/invalid
  // Also set up 'keyup' handlers to validate fields while typing
  const debounced_onFieldUpdated = options.onChange ? debounce(options.onChange, 150) : null;
  const fields = form.querySelectorAll('input, select');
  fields.forEach(field => {
    if (field.type == 'hidden') {
      return;
    }

    field.addEventListener('change', () => {
      // Remove 'untouched' class when the field is changed (like Angular's ng-untouched)
      if (field.value) {
        field.classList.remove('untouched');
      }
      if (options.onFieldChanged) {
        options.onFieldChanged(field);
      }
    });

    if (debounced_onFieldUpdated) {
      field.addEventListener('keyup', () => {
        debounced_onFieldUpdated(field);
      });
    }

    // Also add an 'untouched' class to each field
    field.classList.add('untouched');
  });
}

// Simple form validation used for login, reset password, etc.
// This is not used for the application form or the change password form
//
// Supported options:
// - setFocus: Set focus on the first invalid field
// - setInvalidClass: Set the 'invalid-value' class on invalid fields?
// - clearInvalidClass: Clear the 'invalid-value' class on valid fields?
// - showFieldErrors: Show field errors
// - onlyShowFirstError: Only show the first error
// - onFieldChanged: Callback function to be called when a field is changed
// - onChange: Callback function to be called when a field is changed
export function validateForm(form, customOptions = {}) {
  if (customOptions) {
    options = { ...options, ...customOptions };
  }

  const inputs = form.querySelectorAll('input');
  let isValid = true;

  let hasSetFocus = false;

  for (let i = 0; i < inputs.length; i++) {
    const input = inputs[i];

    if (input.type === 'radio') {
      // Handle radio buttons as a group
      isValid = isRadioChecked(form, input.name);
    } else if (input.required && !input.value.trim()) {
      isValid = false;
    }

    if (input.type == 'text' && input.pattern) {
      const regex = new RegExp(input.pattern);
      if (!regex.test(input.value)) {
        isValid = false;
      }
    }

    if (!isValid) {
      if (options.setInvalidClass) {
        input.classList.add('invalid-value');
        // Add an input handler the class if the user types something in the field or selects a radiobox in the radio group
        bindInputHandler(input);
      }

      if (options.showFieldErrors) {
        showFieldError(input);
      }

      if (options.setFocus && !hasSetFocus) {
        input.focus();
        hasSetFocus = true;
      }
    } else {
      // Hide any previous validation errors
      clearFieldError(input, options.clearInvalidClass);
    }

    if (options.onlyShowFirstError && !isValid) {
      break;
    }
  }

  return isValid;
}

export function hasInvalidFields(form) {
  const inputs = form.querySelectorAll('.invalid-value');
  return inputs.length > 0;
}

function isRadioChecked(form, name) {
  const checkedRadio = form.querySelector('input[name="' + name + '"]:checked');
  return !!checkedRadio;
}

function bindInputHandler(inputElem) {
  // Add an input handler to remove the class, but only if we haven't already added it
  if (!inputElem.hasValidationEventListener) {
    let elems = [inputElem]
    if (inputElem.type === 'radio') {
      elems = inputElem.form.querySelectorAll('input[name="' + inputElem.name + '"]');
    }

    elems.forEach(elem => {
      elem.addEventListener('input', () => {
        onInput(elem);
      });
      elem.hasValidationEventListener = true;
    });
  }
}

function onInput(inputElem) {
  clearFieldError(inputElem);
}

function showFieldError(inputElem, msg) {
  // Use the provided msg if one was passed to the function (used for displaying warnings about invalid chars)
  if (!msg) {
    msg = getFieldErrorMessage(inputElem);
  }

  if (!msg) {
    return;
  }

  // Find the element that the error div should be appended after
  let refElem = getFieldErrorElement(inputElem);

  const nextSibling = refElem.nextElementSibling;
  if (nextSibling && nextSibling.classList.contains('validation-text')) {
    nextSibling.textContent = msg;
    slideDown( nextSibling );
  } else {
    const div = document.createElement('div');
    div.classList.add('validation-text');
    div.classList.add('compact');
    div.textContent = msg;
    div.style.display = 'none';
    // Append after the current input
    refElem.insertAdjacentElement('afterend', div);
    slideDown(div);
  }
}

function showInvalidCharsError(inputElem, invalidChars, msg) {
  if (!msg) {
    msg = __('Vores system kan desværre ikke håndtere disse tegn:');
  }
  showFieldError(inputElem, msg + ' ' + invalidChars);
}

function clearFieldError(inputElem, clearInvalidClass = true) {
  if (clearInvalidClass && inputElem.classList.contains('invalid-value')) {
    inputElem.classList.remove('invalid-value');
  }

  const refElem = getFieldErrorElement(inputElem);
  if (refElem.nextElementSibling && refElem.nextElementSibling.classList.contains('validation-text')) {
    slideUp( refElem.nextElementSibling );
  }
}

function getFieldErrorElement(inputElem) {
  // Find the element that the error div should be appended after
  let refElem = inputElem;
  if (inputElem.type == 'radio') {
    refElem = inputElem.closest('.radio-group') || inputElem.parent;
  }
  if (inputElem.type == 'checkbox') {
    refElem = inputElem.closest('.checkbox-styling') || inputElem.parent;
  }

  return refElem;
}

function getFieldErrorMessage(inputElem) {
  let msg = '';

  // Check for custom error message attribute
  if (inputElem.dataset.errorMessage) {
    return inputElem.dataset.errorMessage;
  }

  // Handle radio groups where the group element is passed directly to this function
  if (inputElem.classList.contains('radio-group')) {
    msg = __('Vælg en af disse muligheder');
    return msg;
  }

  switch (inputElem.tagName.toUpperCase()) {
    case 'INPUT':
      const type = inputElem.type.toLowerCase();
      switch (type) {
        case 'text':
        case 'password':
          msg =  __('Udfyld dette felt');
          break;
        case 'number':
          msg =  __('Udfyld dette felt med et tal');
          break;
        case 'email':
          msg =  __('Udfyld dette felt med en korrekt e-mail');
          break;
        case 'tel':
          msg =  __('Udfyld dette felt med et korrekt telefonnummer');
          break;
        case 'radio':
          msg =  __('Vælg en af disse muligheder');
          break;
        case 'checkbox':
          msg = __('Udfyld dette felt');
          break;
        case 'file':
          msg = __('Vælg en fil');
          break;
        default:
          msg =  __('Udfyld dette felt / Vælg en mulighed');
          break;
      }
      break;
    case 'TEXTAREA':
      msg =  __('Udfyld dette felt');
      break;
    case 'SELECT':
      msg =  __('Vælg en af disse muligheder');
      break;
    default:
      msg =  __('Udfyld dette felt / Vælg en mulighed');
      break;
  }

  // Handle the birthday selects
  if (inputElem.getAttribute('validation-type') == 'date') {
    // Don't show a message (only highlight with red border)
    msg = '';
  }

  return msg;
}

/**
 * Helper function for showing a form error message for the entire form
 */
function showFormError(formElem, msg, allowHTML) {
  const submitBtn = formElem.querySelector('button[type="submit"]');
  if (!submitBtn) {
    console.log('Cannot show form error - no submit button found for form', formElem)
    return;
  }

  // TODO: Remove the extra code, depending on what we decide
  const showBefore = true;

  const errorDiv = showBefore ? submitBtn.previousElementSibling : submitBtn.nextElementSibling;
  if (!errorDiv || !errorDiv.classList.contains('validation-text')) {
    const div = document.createElement('div');
    div.classList.add('validation-text');
    if (allowHTML) {
      div.innerHTML = msg;
    } else {
      div.textContent = msg;
    }
    div.style.display = 'none';
    if (showBefore) {
      div.classList.add('mb24');
    }

    // Append before or after the submit button
    submitBtn.insertAdjacentElement(showBefore ? 'beforebegin' : 'afterend', div);
    slideDown(div);
  } else {
    if (allowHTML) {
      errorDiv.innerHTML = msg;
    } else {
      errorDiv.textContent = msg;
    }
    slideDown(errorDiv);
  }
}

function hideFormError(formElem, immediate) {
  const submitBtn = formElem.querySelector('button[type="submit"]');
  if (!submitBtn) {
    console.log('Cannot hide form error - no submit button found for form', formElem)
    return;
  }

  // TODO: Remove the extra code, depending on what we decide
  const showBefore = true;

  const errorDiv = showBefore ? submitBtn.previousElementSibling : submitBtn.nextElementSibling;
  if (errorDiv && errorDiv.classList.contains('validation-text')) {
    if (immediate) {
      errorDiv.style.display = 'none'
    } else {
      slideUp(errorDiv);
    }
  }
}

function showFormSuccessMsg(formElem, msg, extraClasses, allowHTML) {
  const submitBtn = formElem.querySelector('button[type="submit"]');
  if (!submitBtn) {
    console.log('Cannot show form success - no submit button found for form', formElem)
    return;
  }

  const successDiv = submitBtn.nextElementSibling;
  if (!successDiv || !successDiv.classList.contains('form-success')) {
    const div = document.createElement('div');
    div.classList.add('form-success');
    if (extraClasses) {
      div.classList.add(...extraClasses.split(' '));
    }
    if (allowHTML) {
      div.innerHTML = msg;
    } else {
      div.textContent = msg;
    }
    div.style.display = 'none';

    // Append after the submit button
    submitBtn.insertAdjacentElement('afterend', div);
    slideDown(div);
  } else {
    if (allowHTML) {
      successDiv.innerHTML = msg;
    } else {
      successDiv.textContent = msg;
    }
    successDiv.classList.add('form-success');
    slideDown(successDiv);
  }
}

function hideFormSuccessMsg(formElem) {
  const submitBtn = formElem.querySelector('button[type="submit"]');
  if (!submitBtn) {
    console.log('Cannot show form success - no submit button found for form', formElem)
    return;
  }

  const successDiv = submitBtn.nextElementSibling;
  if (successDiv && successDiv.classList.contains('form-success')) {
    slideUp(successDiv);
  }
}

// Helper function to flatten nested objects into PHP-style array notation
function flattenObject(obj, prefix = '') {
  const flattened = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}[${key}]` : key;

      if (obj[key] === null) {
        // Convert null to empty strings
        flattened[newKey] = '';
      } else if (obj[key] === false) {
        flattened[newKey] = '0';
      } else if (obj[key] === true) {
        flattened[newKey] = '1';
      } else if ('File' in window && obj[key] instanceof File) {
        // Append files unmodified
        flattened[newKey] = obj[key];
      } else if (Array.isArray(obj[key])) {
        // Flatten arrays with indexed keys for PHP
        obj[key].forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            // Recursively flatten nested objects in arrays
            Object.assign(flattened, flattenObject(item, `${newKey}[${index}]`));
          } else {
            flattened[`${newKey}[${index}]`] = item;
          }
        });
      } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        // Recursively flatten nested objects
        Object.assign(flattened, flattenObject(obj[key], newKey));
      } else {
        flattened[newKey] = obj[key];
      }
    }
  }

  return flattened;
}

/**
 * Helper function for submitting a form using fetch
 *
 * @param {Object|HTMLElement} params - Form data to send or form element
 * @param {Object} options - Optional configuration object
 * @param {boolean} options.useFormData - Whether to use FormData (for file uploads)
 * @param {boolean} options.suppressServerErrors - Whether to suppress server-side error messages
 *
 * @returns {Promise<Object>} Response data from the server
 */
async function postFormData(params, options = {}) {
  let formData;
  const headers = {};

  const {
    useFormData = false,
    suppressServerErrors = false,
    extraParams = null, // Any extra parameters to pass to the form data (which is useful when passing a form element) - currently used by the vacation calendar
  } = options;

  // If params is a form element and useFormData is true, create FormData directly from the form
  if (params && params.tagName === 'FORM') {
    formData = new FormData(params);
  } else {
    const flattenedParams = flattenObject(params);

    // FormData should be used when uploading files etc.
    if (useFormData) {
      formData = new FormData();
      Object.keys(flattenedParams).forEach(key => {
        formData.append(key, flattenedParams[key]);
      });
    } else {
      formData = new URLSearchParams(flattenedParams);
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
  }

  if (extraParams) {
    Object.keys(extraParams).forEach(key => {
      formData.append(key, extraParams[key]);
    });
  }

  const response = await fetch(window.ajaxurl, {
    method: 'POST',
    headers: headers,
    body: formData,
    credentials: 'same-origin'
  });

  if (!response.ok) {
    // First check if a JSON object was returned with a success and a non-empty message property
    // This is for example returned from buy_gifts when the distributor has stopped (in which case the AJAX handler sends a 404 response code and a JSON response)
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const responseData = await response.json();
      if (responseData && responseData.success !== undefined) {
        return responseData;
      }
    }

    console.error(`HTTP error! status: ${response.status}`);

    if (suppressServerErrors) {
      // Simply return false, instead of returning an error object
      // This is used when checking email uniqueness where we don't want to show error messages if the server call fails
      return false;
    }

    return {
      success: false,
      message: __('Der skete en serverfejl. Prøv igen senere.'),
    }
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return await response.json();
  } else {
    console.error("Expected JSON but got: " + contentType);

    if (suppressServerErrors) {
      // Simply return false, instead of returning an error object
      // This is used when checking email uniqueness where we don't want to show error messages if the server call fails
      return false;
    }

    return {
      success: false,
      message: __('Der skete en serverfejl. Prøv igen senere.'),
    }
  }
}

// Helper function for showing a form error if a fetch/AJAX error occurs when submitting the form
function handleFetchError(error, formElem) {
  let msg;
  if (error.status === 500 || error.status === 400) {
    msg = __('Der skete en serverfejl. Prøv igen senere.');
  } else {
    msg = __('Fejl i forbindelse til DiSy. Prøv igen.');
  }
  showFormError(formElem, msg);
}

// Additional validation functions
function isEmailValid(input) {
  const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  if (input.match(validRegex)) {
    // Assume the domain will have at least two subs (to be consistent with the WordPress is_email() function)
    const domain = input.split('@')[1];
    const subs = domain.split('.');
    if (subs.length < 2) {
      return false;
    }

    // Don't allow email addresses with @duck.com
    if (input.indexOf('@duck.com') != -1) {
      return false;
    }
    return true;
  } else {
    return false;
  }
}

function isPhoneNumberValid(input) {
  var validRegex = /^\d{8}$/;
  if (window.sdr_site) {
    // Mobile numbers always start with 07 in Sweden, followed by 8 digits (but the 0 is optional)
    validRegex = /^0?7\d{8}$/;
  }
  var trimmed = input.replace(/[- ]/g, ''); // Use a regex to replace all the occurrences of spaces and dashes
  if (trimmed.match(validRegex)) {
    return true;
  } else {
    return false;
  }
}

function isPZipcodeValid(input) {
  var validRegex = /^[0-9]{4}$/;
  var str = input;
  if (window.sdr_site) {
    validRegex = /^[0-9]{5}$/;
    var str = str.replace(/[ ]/g, ''); // Use a regex to replace all the occurrences of spaces
  }
  if (str.match(validRegex)) {
    return true;
  } else {
    return false;
  }
}

function isValidDate(day, month, year) {
  let valid = true;
  if (day && month && year) {
    const d = new Date(year, month - 1, day);
    if (d.getDate() != day || (d.getMonth() + 1) != month || d.getFullYear() != year) {
      valid = false;
    }
  }
  return valid;
}

function isUnderThirteen(day, month, year) {
  let underThirteen = false;
  if (day && month && year) {
    const bday = dayjs(day+'-'+month+'-'+year,'D-M-YYYY');
    const diff = dayjs().diff(bday,'months',true);
    underThirteen = (diff < 156);
  }
  return underThirteen;
};

function isUnderEighteen(day, month, year) {
  let underEighteen = false;
  // Important: On SDR, this function checks if you're under 16 (not 18)
  const months = 12 * (window.sdr_site ? 16 : 18);
  if (day && month && year) {
    const bday = dayjs(day+'-'+month+'-'+year,'D-M-YYYY');
    const diff = dayjs().diff(bday,'months',true);
    underEighteen = (diff < months);
  }

  return underEighteen;
};

function isOverEighteen(day, month, year) {
  let overEighteen = false;
  // Important: This function also checks for 18  on SDR (not 16, which we do in the underEighteen() function)
  if (day && month && year) {
    const bday = dayjs(day+'-'+month+'-'+year,'D-M-YYYY');
    const diff = dayjs().diff(bday,'months',true);
    overEighteen = (diff >= 216);
  }

  return overEighteen;
};

// Password validation functions for the application and stamdata forms
function stringHasLetters(pw) {
  // Validate capital letters
  var upperCaseLetters = /[A-ZÆØÅ]/g;
  var lowerCaseLetters = /[a-zøæå]/g;
  if (pw && pw.match(upperCaseLetters) && pw.match(lowerCaseLetters)) {
    return true;
  }
  return false;
}

function stringHasNumber(pw) {
  // Validate numbers
  var numbers = /[0-9]/g;
  if (pw && pw.match(numbers)) {
    return true;
  }
  return false;
}

function stringHasPasswordSpecialChar(pw) {
  // Validate special chars by removing all the letters and numbers
  if (pw) {
    var specialChars = pw.replace(/[a-zæøåA-ZÆØÅ0-9]+/g, '');
    if (specialChars.length) {
      // Check if any of the special are invalid disy characters
      var invalidChars = getInvalidDiSyCharsInString(specialChars, specialChars.length);
      if (invalidChars.length == specialChars.length) {
        // All the special chars are invalid
        return false;
      }
      return true;
    }
  }
  return false;
}

function validPasswordLength(pw) {
  // Validate length
  if (pw && pw.length >= 8) {
    return true;
  }
  return false;
}

function validPasswordMaxLength(pw) {
  // Validate length
  if (pw && pw.length > 30) {
    return false;
  }
  return true;
}

// Helper function to check for invalid characters that cause DiSy to return a "Handlingen fejlede" error
const validChars = " !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ^_`abcdefghijklmnopqrstuvwxyz~¡¢£¤¥§·¸ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ\n\r";
const validCharsRegex = new RegExp('^['+validChars+']+$');
function containsInvalidDiSyChars(str) {
  if (!str) return false;
  return !validCharsRegex.test(str);
}

function getInvalidDiSyCharsInString(str, limit) {
  if (!str) return false;
  if (!limit) limit = 5;
  var invalidChars = '';
  var count = 0;
  for (var i=0, l=str.length; i<l; i++) {
    var char = str[i];
    // Check if the char is a "high surrogate" and if so, include the next character
    if (char.charCodeAt(0) >= 0xD800 && char.charCodeAt(0) <= 0xDBFF) {
      if (i+1 < l) {
        char += str[i+1];
        i++;
      }
    }
    if (!validChars.includes(char) && !invalidChars.includes(char)) {
      count++;
      // Only append the characters if we have not yet exceededd the limit
      if (limit && limit >= 1 && count <= limit) {
        invalidChars += char;
      }
    }
  }

  if (limit && limit >= 1 && count > limit) {
    invalidChars += ' ' + __('m.fl.');
  }
  return invalidChars;
}

export default {
  initFormEvents,
  validateForm,
  hasInvalidFields,
  showFormError, // For showing an error message for the entire form
  hideFormError, // For hiding an error message for the entire form
  showFormSuccessMsg,
  hideFormSuccessMsg,
  showFieldError,  // For showing an error message for a single field
  clearFieldError, // For hiding an error message for a single field
  showInvalidCharsError,
  // AJAX helpers
  postFormData,
  handleFetchError,
  // Additional validation functions
  isEmailValid,
  isPhoneNumberValid,
  isPZipcodeValid,
  isValidDate,
  isUnderThirteen,
  isUnderEighteen,
  isOverEighteen,
  // Password validation functions
  stringHasLetters,
  stringHasNumber,
  stringHasPasswordSpecialChar,
  validPasswordLength,
  validPasswordMaxLength,
  // Validation functions for invalid DiSy characters
  containsInvalidDiSyChars,
  getInvalidDiSyCharsInString,
};

